import * as THREE from "three";

/**
 * A procedural hill. The tower sits on the summit at the world origin (0,0,0);
 * terrain falls away radially so the structure reads as perched on a hilltop.
 * A flat pad of radius `padRadius` keeps the foundation area level.
 */

// cheap deterministic value noise (no Math.random — keeps builds reproducible)
function hash2(x: number, z: number): number {
  const s = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
  return s - Math.floor(s); // 0..1
}
function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}
function valueNoise(x: number, z: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const xf = x - xi;
  const zf = z - zi;
  const a = hash2(xi, zi);
  const b = hash2(xi + 1, zi);
  const c = hash2(xi, zi + 1);
  const d = hash2(xi + 1, zi + 1);
  const u = smooth(xf);
  const v = smooth(zf);
  return THREE.MathUtils.lerp(
    THREE.MathUtils.lerp(a, b, u),
    THREE.MathUtils.lerp(c, d, u),
    v,
  );
}
function fbm(x: number, z: number): number {
  let sum = 0;
  let amp = 0.5;
  let freq = 1;
  for (let o = 0; o < 4; o++) {
    sum += amp * (valueNoise(x * freq, z * freq) * 2 - 1);
    amp *= 0.5;
    freq *= 2.07;
  }
  return sum; // ~ -1..1
}

export interface HillOptions {
  extent?: number; // half-width of the terrain patch (m)
  segments?: number;
  peakDrop?: number; // how far terrain falls from summit to edge (m)
  falloff?: number; // radial scale of the dome (m)
  roughness?: number; // amplitude of surface bumps (m)
  padRadius?: number; // flat area around the summit (m)
}

/** Height of the hill surface at world (x,z). Summit is 0 at the origin. */
export function hillHeight(x: number, z: number, opts: Required<HillOptions>): number {
  const r = Math.hypot(x, z);
  // smooth dome: 0 at centre, → -peakDrop far out
  const dome = -opts.peakDrop * (1 - Math.exp(-(r * r) / (opts.falloff * opts.falloff)));
  // surface roughness, faded out inside the pad and gently ramped beyond it
  const padFade = THREE.MathUtils.smoothstep(r, opts.padRadius, opts.padRadius * 2.2);
  const bumps = fbm(x * 0.03, z * 0.03) * opts.roughness * padFade;
  return dome + bumps;
}

/** Default hill parameters — shared by the mesh and the ground-height query. */
export const DEFAULT_HILL: Required<HillOptions> = {
  extent: 220,
  segments: 240,
  peakDrop: 70,
  falloff: 95,
  roughness: 9,
  padRadius: 12,
};

/** Terrain height at world (x,z) using the default hill — for anchors/foundations. */
export function groundHeight(x: number, z: number): number {
  return hillHeight(x, z, DEFAULT_HILL);
}

export function buildHill(options: HillOptions = {}): THREE.Mesh {
  const opts: Required<HillOptions> = { ...DEFAULT_HILL, ...options };

  const geo = new THREE.PlaneGeometry(
    opts.extent * 2,
    opts.extent * 2,
    opts.segments,
    opts.segments,
  );
  geo.rotateX(-Math.PI / 2); // into the XZ plane, +Y up

  const pos = geo.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);

  const grass = new THREE.Color(0x3a5a34);
  const grassDry = new THREE.Color(0x5b6b3a);
  const rock = new THREE.Color(0x4a4640);
  const c = new THREE.Color();

  for (let idx = 0; idx < pos.count; idx++) {
    const x = pos.getX(idx);
    const z = pos.getZ(idx);
    const y = hillHeight(x, z, opts);
    pos.setY(idx, y);

    // colour by slope: flatter → grass, steeper → rock
    const e = 1.0;
    const hL = hillHeight(x - e, z, opts);
    const hR = hillHeight(x + e, z, opts);
    const hD = hillHeight(x, z - e, opts);
    const hU = hillHeight(x, z + e, opts);
    const slope = Math.min(1, Math.hypot(hR - hL, hU - hD) / (2 * e) / 1.1);
    c.copy(grass).lerp(grassDry, THREE.MathUtils.clamp((-y) / opts.peakDrop, 0, 1) * 0.6);
    c.lerp(rock, smooth(THREE.MathUtils.clamp(slope, 0, 1)));
    colors[idx * 3] = c.r;
    colors[idx * 3 + 1] = c.g;
    colors[idx * 3 + 2] = c.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.95,
    metalness: 0.0,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  return mesh;
}
